import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { FaWallet, FaBars, FaTimes, FaMoon, FaSun, FaExclamationTriangle, FaCog, FaUser } from 'react-icons/fa';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [mobileMenuOpen]);

  const mobileLinkClass = (path) => `flex min-h-11 items-center rounded-xl px-4 py-3 text-sm font-semibold transition-colors duration-300 ease-in-out ${location.pathname === path
    ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300'
    : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
    }`;

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <nav className="bg-white/80 backdrop-blur-md dark:bg-gray-900/90 shadow-sm sticky top-0 z-50 border-b border-gray-100 dark:border-gray-800 transition-colors duration-300 ease-in-out supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center flex-shrink-0 mr-0 xl:mr-6 min-w-0">
              <Link to="/" onClick={scrollToTop} className="flex items-center gap-2 sm:gap-3 min-w-0">
                <FaWallet className="text-primary-600 dark:text-primary-400 text-2xl sm:text-3xl flex-shrink-0" />
                <span className="inline text-sm min-[390px]:text-base md:text-2xl font-bold text-gray-800 dark:text-white whitespace-nowrap">Döviz Defteri</span>
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="hidden xl:flex items-center space-x-3 flex-1 justify-end">
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                title={darkMode ? 'Açık Mod' : 'Karanlık Mod'}
              >
                {darkMode ? (
                  <FaSun className="text-yellow-400 text-lg" />
                ) : (
                  <FaMoon className="text-gray-700 text-lg" />
                )}
              </button>



              <Link
                to="/"
                onClick={scrollToTop}
                className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap"
              >
                Ana Sayfa
              </Link>
              <Link
                to="/doviz"
                onClick={scrollToTop}
                className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap"
              >
                Döviz
              </Link>
              <Link
                to="/altin"
                onClick={scrollToTop}
                className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap"
              >
                Altın
              </Link>
              <Link
                to="/parite"
                onClick={scrollToTop}
                className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap"
              >
                Pariteler
              </Link>

              {/* Separator */}
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-2"></div>

              {user ? (
                <>
                  <Link
                    to="/portfolio"
                    className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap"
                  >
                    Portföyüm
                  </Link>
                  <Link
                    to="/alerts"
                    className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap"
                  >
                    Alarmlar
                  </Link>
                  <Link
                    to="/add-asset"
                    className="bg-primary-600 text-white px-3 xl:px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition whitespace-nowrap"
                  >
                    Varlık Ekle
                  </Link>
                  {user.role === 'admin' && (
                    <Link
                      to="/admin/dashboard"
                      className="bg-purple-600 text-white px-3 xl:px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 transition whitespace-nowrap"
                    >
                      Admin
                    </Link>
                  )}

                  {/* User Profile - Desktop */}
                  <Link
                    to="/settings"
                    className="hidden xl:flex items-center space-x-2 px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    {user.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover border-2 border-blue-500"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {user.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-gray-700 dark:text-gray-200 text-xs xl:text-sm whitespace-nowrap font-medium">{user.name}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-xs flex items-center gap-1">
                        <FaCog className="text-[10px]" />
                        Profil
                      </span>
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="bg-red-500 text-white px-3 xl:px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600 transition whitespace-nowrap"
                  >
                    Çıkış
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-2 xl:px-3 py-2 rounded-md text-sm font-medium transition whitespace-nowrap"
                  >
                    Giriş
                  </Link>
                  <Link
                    to="/register"
                    className="bg-primary-600 text-white px-3 xl:px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition whitespace-nowrap"
                  >
                    Kayıt Ol
                  </Link>
                </>
              )}
            </div>

            {/* Mobile shortcuts and menu button */}
            <div className="xl:hidden flex items-center gap-1 sm:gap-2">
              {user && (
                <>
                  <Link to="/portfolio" aria-label="Portföyüm" className="flex h-9 w-9 min-[390px]:w-auto min-[390px]:px-2 items-center justify-center gap-1.5 rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors duration-300 ease-in-out">
                    <FaWallet size={17} />
                    <span className="hidden min-[390px]:inline text-xs font-semibold">Portföy</span>
                  </Link>
                  <Link to="/settings" aria-label="Profil" className="flex h-9 w-9 min-[390px]:w-auto min-[390px]:px-2 items-center justify-center gap-1.5 rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors duration-300 ease-in-out">
                    <FaUser size={17} />
                    <span className="hidden min-[390px]:inline text-xs font-semibold">Profil</span>
                  </Link>
                </>
              )}
              <button
                type="button"
                onClick={toggleDarkMode}
                aria-label={darkMode ? 'Açık moda geç' : 'Karanlık moda geç'}
                title={darkMode ? 'Açık Mod' : 'Karanlık Mod'}
                className="flex h-9 w-9 min-[390px]:w-auto min-[390px]:px-2 items-center justify-center gap-1.5 rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors duration-300 ease-in-out"
              >
                {darkMode ? <FaSun className="text-yellow-400" size={17} /> : <FaMoon size={17} />}
                <span className="hidden min-[390px]:inline text-xs font-semibold">Tema</span>
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex h-10 w-10 items-center justify-center text-gray-700 dark:text-gray-200 hover:text-primary-600 focus:outline-none rounded-lg transition-colors duration-300 ease-in-out"
                aria-label={mobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-navigation-drawer"
              >
                <FaBars size={22} />
              </button>
            </div>
          </div>
        </div>

      </nav>

      {/* Always mounted so both opening and closing slide transitions complete. */}
      <div
        className={`xl:hidden fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm transition-opacity duration-300 ease-in-out ${mobileMenuOpen ? 'visible opacity-100' : 'invisible pointer-events-none opacity-0'}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />
      <aside
        id="mobile-navigation-drawer"
        className={`xl:hidden fixed right-0 top-0 z-[70] h-dvh w-[280px] max-w-[85vw] overflow-y-auto border-l border-gray-200 bg-white text-slate-900 shadow-2xl dark:border-slate-800 dark:bg-slate-950 dark:text-white transition-[transform,opacity,background-color,border-color,color,box-shadow] duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'}`}
        aria-hidden={!mobileMenuOpen}
      >
        <div className="flex min-h-16 items-center justify-between border-b border-gray-200 px-4 dark:border-slate-800 transition-colors duration-300 ease-in-out">
          <Link to="/" className="flex items-center gap-2 font-bold" onClick={scrollToTop}>
            <FaWallet className="text-primary-600 dark:text-primary-400" />
            <span>Döviz Defteri</span>
          </Link>
          <button onClick={() => setMobileMenuOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-300 ease-in-out" aria-label="Menüyü kapat">
            <FaTimes size={21} />
          </button>
        </div>

        <div className="space-y-1 p-3">
          <Link to="/" className={mobileLinkClass('/')} onClick={scrollToTop}>Ana Sayfa</Link>
          <Link to="/doviz" className={mobileLinkClass('/doviz')} onClick={scrollToTop}>Döviz</Link>
          <Link to="/altin" className={mobileLinkClass('/altin')} onClick={scrollToTop}>Altın</Link>
          <Link to="/parite" className={mobileLinkClass('/parite')} onClick={scrollToTop}>Pariteler</Link>

          <div className="my-3 border-t border-gray-200 dark:border-slate-800 transition-colors duration-300 ease-in-out" />

          {user ? (
            <>
              <Link to="/portfolio" className={mobileLinkClass('/portfolio')}>Portföyüm</Link>
              <Link to="/alerts" className={mobileLinkClass('/alerts')}>Alarmlar</Link>
              <Link to="/settings" className={mobileLinkClass('/settings')}>Profil</Link>
              <Link to="/add-asset" className={mobileLinkClass('/add-asset')}>Varlık Ekle</Link>
              {user.role === 'admin' && <Link to="/admin/dashboard" className={mobileLinkClass('/admin/dashboard')}>Admin Panel</Link>}
            </>
          ) : (
            <>
              <Link to="/login" className={mobileLinkClass('/login')}>Giriş Yap</Link>
              <Link to="/register" className="flex min-h-11 items-center justify-center rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors duration-300 ease-in-out">Kayıt Ol</Link>
            </>
          )}

          <button onClick={toggleDarkMode} className="flex min-h-11 w-full items-center rounded-xl px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors duration-300 ease-in-out">
            {darkMode ? <FaSun className="mr-3 text-yellow-400" /> : <FaMoon className="mr-3 text-slate-600" />}
            {darkMode ? 'Açık Mod' : 'Karanlık Mod'}
          </button>

          {user && (
            <button onClick={handleLogout} className="flex min-h-11 w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30 transition-colors duration-300 ease-in-out">
              Çıkış
            </button>
          )}
        </div>
      </aside>

      {/* Email Doğrulama Uyarısı */}
      {user && !user.isVerified && (
        <div className="bg-yellow-500 dark:bg-yellow-600 text-white px-4 py-3 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-2">
              <FaExclamationTriangle className="text-xl" />
              <span className="font-medium">Email adresiniz doğrulanmamış!</span>
              <span className="hidden sm:inline text-sm">Lütfen email adresinizi doğrulayın.</span>
            </div>
            <Link
              to="/verify-email"
              className="bg-white text-yellow-600 px-4 py-1 rounded-md font-medium hover:bg-yellow-50 transition text-sm mt-2 sm:mt-0"
            >
              Şimdi Doğrula
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
